// Every visual decision reads a --consent-* custom property. Custom properties
// cross the shadow boundary, so a site themes the UI from its own stylesheet
// (or through the `theme` option) without reaching into the shadow root.
export const STYLES = `
:host {
  all: initial;
  --_font: var(--consent-font, inherit);
  --_bg: var(--consent-bg, #ffffff);
  --_text: var(--consent-text, #1b1f24);
  --_muted: var(--consent-muted, #57606a);
  --_border: var(--consent-border, #d0d7de);
  --_button-bg: var(--consent-button-bg, #f6f8fa);
  --_button-text: var(--consent-button-text, var(--_text));
  --_button-border: var(--consent-button-border, #8c959f);
  --_accent: var(--consent-accent, #0a5fb4);
  --_accent-text: var(--consent-accent-text, #ffffff);
  --_radius: var(--consent-radius, 12px);
  --_button-radius: var(--consent-button-radius, 8px);
  --_shadow: var(--consent-shadow, 0 12px 40px -12px rgb(0 0 0 / 0.35));
  --_backdrop: var(--consent-backdrop, rgb(0 0 0 / 0.5));
  --_z: var(--consent-z, 2147483000);
  font-family: var(--_font);
  font-size: 15px;
  line-height: 1.55;
  color: var(--_text);
}

*, *::before, *::after { box-sizing: border-box; }
[hidden] { display: none !important; }
p, h2, h3 { margin: 0; }
a { color: inherit; text-decoration: underline; text-underline-offset: 3px; }

.banner {
  position: fixed;
  inset: auto 16px 16px 16px;
  z-index: var(--_z);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px 28px;
  max-width: 1080px;
  max-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 18px 22px;
  overflow: auto;
  border: 1px solid var(--_border);
  border-radius: var(--_radius);
  background: var(--_bg);
  box-shadow: var(--_shadow);
  color: var(--_text);
  font: inherit;
}

:host([data-position="bottom-left"]) .banner,
:host([data-position="bottom-right"]) .banner {
  grid-template-columns: 1fr;
  max-width: 420px;
}
:host([data-position="bottom-left"]) .banner { right: auto; }
:host([data-position="bottom-right"]) .banner { left: auto; }

.banner-text { display: grid; gap: 4px; }
.title { font-size: 1.05em; font-weight: 600; }
.body { color: var(--_muted); }

.brand { display: flex; align-items: center; gap: 10px; }
.logo { display: block; max-width: 140px; max-height: 28px; object-fit: contain; }

.actions { display: flex; flex-wrap: wrap; gap: 8px; }

/* Accepting is never louder than refusing: one style for every choice. */
.button {
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid var(--_button-border);
  border-radius: var(--_button-radius);
  background: var(--_button-bg);
  color: var(--_button-text);
  font: inherit;
  font-weight: 500;
  cursor: pointer;
}
.button:hover { filter: brightness(0.96); }
.button:disabled { cursor: progress; opacity: 0.6; }

.button:focus-visible,
.close:focus-visible,
a:focus-visible {
  outline: 2px solid var(--_accent);
  outline-offset: 2px;
}

.status { color: var(--_muted); font-size: 0.9em; }
.status:empty { display: none; }

.dialog {
  width: min(680px, calc(100% - 24px));
  max-height: min(88vh, 820px);
  margin: auto;
  padding: 0;
  overflow: auto;
  overscroll-behavior: contain;
  border: 1px solid var(--_border);
  border-radius: var(--_radius);
  background: var(--_bg);
  box-shadow: var(--_shadow);
  color: var(--_text);
  font: inherit;
}
.dialog::backdrop { background: var(--_backdrop); }

.panel { display: grid; gap: 16px; padding: 22px 22px 0; }

.head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.head-text { display: grid; gap: 10px; }
.head h2 { font-size: 1.25em; font-weight: 600; }

.close {
  flex: none;
  width: 40px;
  height: 40px;
  border: 1px solid var(--_border);
  border-radius: 50%;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 1.3em;
  line-height: 1;
  cursor: pointer;
}

.categories { display: grid; gap: 10px; margin: 0; padding: 0; list-style: none; }
.category {
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid var(--_border);
  border-radius: calc(var(--_radius) - 2px);
}
.category-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.category h3 { font-size: 1em; font-weight: 600; }
.category p { color: var(--_muted); font-size: 0.9em; }

.switch { position: relative; flex: none; display: inline-flex; align-items: center; gap: 8px; }
.switch input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.switch input:disabled { cursor: not-allowed; }
.track {
  position: relative;
  width: 44px;
  height: 26px;
  border: 1px solid var(--_button-border);
  border-radius: 13px;
  background: var(--_button-bg);
  pointer-events: none;
}
.track::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--_muted);
  transition: transform 160ms ease;
}
.switch input:checked + .track { border-color: var(--_accent); background: var(--_accent); }
.switch input:checked + .track::after { background: var(--_accent-text); transform: translateX(18px); }
.switch input:disabled + .track { opacity: 0.55; }
.switch input:focus-visible + .track { outline: 2px solid var(--_accent); outline-offset: 2px; }
.locked { color: var(--_muted); font-size: 0.8em; white-space: nowrap; }

.note { color: var(--_muted); font-size: 0.85em; }
.empty { color: var(--_muted); font-size: 0.85em; font-style: italic; }

.services { margin-top: 6px; font-size: 0.9em; }
.services summary {
  width: fit-content;
  padding: 2px 0;
  color: var(--_text);
  font-weight: 500;
  cursor: pointer;
}
.services summary:focus-visible { outline: 2px solid var(--_accent); outline-offset: 2px; }
.vendors { display: grid; gap: 8px; margin: 8px 0 0; padding: 0; list-style: none; }
.vendor {
  display: grid;
  gap: 4px;
  padding: 10px 12px;
  border-radius: calc(var(--_radius) - 4px);
  background: var(--_button-bg);
}
.vendor p { color: var(--_muted); }
.vendor p:first-child { color: var(--_text); }
.vendor .meta { font-size: 0.9em; }
.vendor table { width: 100%; margin-top: 4px; border-collapse: collapse; font-size: 0.9em; }
.vendor th {
  padding: 4px 8px 4px 0;
  border-bottom: 1px solid var(--_border);
  color: var(--_muted);
  font-weight: 500;
  text-align: left;
}
.vendor td { padding: 4px 8px 4px 0; vertical-align: top; overflow-wrap: anywhere; }
.vendor code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.95em; }

/* The choices stay in reach while the categories scroll. */
.footer {
  position: sticky;
  bottom: 0;
  display: grid;
  gap: 10px;
  margin: 0 -22px;
  padding: 14px 22px 18px;
  border-top: 1px solid var(--_border);
  background: var(--_bg);
}
.footer .actions { justify-content: flex-end; }

@media (max-width: 720px) {
  .banner,
  :host([data-position="bottom-left"]) .banner,
  :host([data-position="bottom-right"]) .banner {
    inset: auto 12px 12px 12px;
    grid-template-columns: 1fr;
    max-width: none;
    padding: 16px;
  }
  .actions .button { flex: 1 1 auto; padding: 0 12px; }
  .footer .actions { display: grid; grid-template-columns: 1fr 1fr; }
  .footer [data-action="save"],
  .footer [data-notice] { grid-column: 1 / -1; }
  .panel { padding: 18px 16px 0; }
  .footer { margin: 0 -16px; padding: 12px 16px 14px; }
}

@media (prefers-reduced-motion: reduce) {
  .track::after { transition: none; }
}
`;
